import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, fmtDateTime, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, MetricCard, BtnPrimary, BtnSecondary, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import {
  Plus, DownloadSimple, Truck, Mountains, Target, Receipt, Printer, CheckCircle, Trash, Eye,
} from "@phosphor-icons/react";
import SmartInput from "@/components/SmartInput";
import ProjectSelect from "@/components/ProjectSelect";
import SendEmailDialog from "@/components/SendEmailDialog";

const TABS = [
  { id: "log", label: "Daily Log" },
  { id: "shifts", label: "Shifts (Totaliser)" },
  { id: "delivery", label: "Delivery Notes" },
  { id: "report", label: "Cumulative Report" },
];

function fmtUSD(n) {
  if (typeof n === "string") return n;  // e.g. backend-masked "— restricted —"
  return `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTonnes(n) {
  return `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} t`;
}

export default function Production() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("log");
  const [products, setProducts] = useState([]);
  const [records, setRecords] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const canEdit = can(user.role, "staff");
  const canManage = can(user.role, "manager");

  // Production log form
  const today = new Date().toISOString().slice(0, 10);
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    record_date: today, product_code: "", tonnes: 0, shift: "full", project_code: "", notes: "",
  });

  // Delivery note form
  const [dnOpen, setDnOpen] = useState(false);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const [dnForm, setDnForm] = useState({
    delivery_date: yesterday, customer: "", project: "", project_code: "",
    product_code: "", tonnes: 0, truck_plate: "", driver_name: "", notes: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ roads_team_signoff: "", contract_manager_signoff: "" });
  const [viewDn, setViewDn] = useState(null);
  const [emailDn, setEmailDn] = useState(null);  // { dn, to, subject, preview }

  // Shift log (totaliser) form
  const [shifts, setShifts] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    shift_date: today, shift: "day", product_code: "",
    totalizer_start: 0, totalizer_end: 0,
    target_tonnes: 2000, justification: "", notes: "",
  });
  const [sendingReport, setSendingReport] = useState(false);

  async function loadShifts() {
    try { const { data } = await api.get("/production/shifts?days=14"); setShifts(data); }
    catch (e) { toast.error(formatError(e)); }
    try { const { data } = await api.get("/production/contract-manager-report/cron-status"); setCronStatus(data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  function openShift() {
    const now = new Date();
    const hour = now.getHours();
    const inferredShift = (hour >= 6 && hour < 18) ? "day" : "night";
    setShiftForm({
      shift_date: today, shift: inferredShift,
      product_code: products[0]?.code || "",
      totalizer_start: 0, totalizer_end: 0,
      target_tonnes: 2000, justification: "", notes: "",
    });
    setShiftOpen(true);
  }
  async function submitShift(e) {
    e.preventDefault();
    const tonnes = Number(shiftForm.totalizer_end) - Number(shiftForm.totalizer_start);
    if (Number(shiftForm.totalizer_end) < Number(shiftForm.totalizer_start)) {
      toast.error("Totaliser end must be ≥ start"); return;
    }
    if (tonnes < Number(shiftForm.target_tonnes) && !shiftForm.justification.trim()) {
      toast.error(`Tonnage ${tonnes.toFixed(2)} is below target ${shiftForm.target_tonnes}t — please justify.`);
      return;
    }
    try {
      await api.post("/production/shifts", {
        shift_date: new Date(shiftForm.shift_date).toISOString(),
        shift: shiftForm.shift,
        product_code: shiftForm.product_code,
        totalizer_start: Number(shiftForm.totalizer_start),
        totalizer_end: Number(shiftForm.totalizer_end),
        target_tonnes: Number(shiftForm.target_tonnes),
        justification: shiftForm.justification || null,
        notes: shiftForm.notes || null,
      });
      toast.success(`Shift logged — ${tonnes.toFixed(2)}t`);
      setShiftOpen(false);
      loadShifts(); loadSummary();
    } catch (err) { toast.error(formatError(err)); }
  }
  async function sendContractReport() {
    setSendingReport(true);
    try {
      const { data } = await api.post("/production/contract-manager-report");
      toast.success(`Report sent to ${data.recipients} manager(s) — ${data.total_tonnes}t in window`);
    } catch (e) { toast.error(formatError(e)); }
    setSendingReport(false);
  }

  async function loadProducts() {
    try { const { data } = await api.get("/production/products"); setProducts(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadRecords() {
    try { const { data } = await api.get("/production/records"); setRecords(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadDeliveries() {
    try { const { data } = await api.get("/delivery-notes"); setDeliveries(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadSummary() {
    try { const { data } = await api.get("/production/summary?days=60"); setSummary(data); }
    catch (e) { toast.error(formatError(e)); }
  }

  useEffect(() => { loadProducts(); loadSummary(); }, []);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/customers?active_only=true"); setCustomers(data); }
      catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);
  useEffect(() => {
    if (tab === "log") loadRecords();
    if (tab === "shifts") loadShifts();
    if (tab === "delivery") loadDeliveries();
    if (tab === "report") loadSummary();
  }, [tab]);

  function openLog() {
    setLogForm({
      record_date: today,
      product_code: products[0]?.code || "",
      tonnes: 0, shift: "full", project_code: "", notes: "",
    });
    setLogOpen(true);
  }
  async function submitLog(e) {
    e.preventDefault();
    try {
      await api.post("/production/records", {
        record_date: new Date(logForm.record_date).toISOString(),
        product_code: logForm.product_code,
        tonnes: Number(logForm.tonnes),
        shift: logForm.shift,
        project_code: logForm.project_code || null,
        notes: logForm.notes,
      });
      toast.success("Production logged");
      setLogOpen(false); loadRecords(); loadSummary();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function deleteRecord(r) {
    if (!window.confirm(`Delete record of ${r.tonnes}t on ${fmtDate(r.record_date)}?`)) return;
    try { await api.delete(`/production/records/${r.id}`); toast.success("Deleted"); loadRecords(); loadSummary(); }
    catch (e) { toast.error(formatError(e)); }
  }

  function openDn() {
    setDnForm({
      delivery_date: yesterday, customer: "", project: "", project_code: "",
      product_code: products[0]?.code || "",
      tonnes: 0, truck_plate: "", driver_name: "", notes: "",
    });
    setDnOpen(true);
  }
  async function submitDn(e) {
    e.preventDefault();
    try {
      await api.post("/delivery-notes", {
        delivery_date: new Date(dnForm.delivery_date).toISOString(),
        customer: dnForm.customer, project: dnForm.project,
        project_code: dnForm.project_code || null,
        product_code: dnForm.product_code, tonnes: Number(dnForm.tonnes),
        truck_plate: dnForm.truck_plate, driver_name: dnForm.driver_name,
        notes: dnForm.notes,
      });
      toast.success("Delivery note created");
      setDnOpen(false); loadDeliveries();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function confirmDn(e) {
    e.preventDefault();
    try {
      await api.post(`/delivery-notes/${confirmOpen.id}/confirm`, confirmForm);
      toast.success("Sign-off recorded");
      setConfirmOpen(null);
      setConfirmForm({ roads_team_signoff: "", contract_manager_signoff: "" });
      loadDeliveries();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function voidDn(d) {
    if (!window.confirm(`Void delivery note ${d.number}?`)) return;
    try { await api.post(`/delivery-notes/${d.id}/void`); toast.success("Voided"); loadDeliveries(); }
    catch (e) { toast.error(formatError(e)); }
  }

  const todayCard = summary?.today || { tonnes: 0, progress_pct: 0 };
  const target = summary?.daily_target || 4000;

  return (
    <div data-testid="production-page">
      <PageHeader
        overline="Module / Production"
        title="Crushing Operations"
        subtitle={`Daily target: ${target.toLocaleString()} tonnes — log production, deliver to roads team, invoice month-end.`}
        actions={
          <>
            {canEdit && tab === "log" && (
              <BtnPrimary onClick={openLog} data-testid="prod-log-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Log Production
              </BtnPrimary>
            )}
            {canEdit && tab === "delivery" && (
              <BtnPrimary onClick={openDn} data-testid="dn-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New Delivery Note
              </BtnPrimary>
            )}
            {canEdit && tab === "shifts" && (
              <>
                <BtnPrimary onClick={openShift} data-testid="shift-create">
                  <Plus size={14} weight="bold" className="inline mr-1.5" /> Log Shift
                </BtnPrimary>
                {canManage && (
                  <BtnSecondary onClick={sendContractReport} disabled={sendingReport} data-testid="shift-send-report">
                    {sendingReport ? "Sending…" : "Send 24h Report"}
                  </BtnSecondary>
                )}
              </>
            )}
          </>
        }
      />

      {/* Top KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <MetricCard
            testId="prod-today" accent="primary"
            label="Today (Tonnes)"
            value={fmtTonnes(todayCard.tonnes)}
            delta={`${todayCard.progress_pct}% of ${target.toLocaleString()}t`}
            deltaLabel="Target"
          />
          {summary.by_product.map((p) => (
            <MetricCard
              key={p.code}
              testId={`prod-product-${p.code}`}
              label={`${p.code} · 60d`}
              value={fmtTonnes(p.tonnes)}
              delta={fmtUSD(p.revenue_usd)}
              deltaLabel="Revenue"
              accent="default"
            />
          ))}
          <MetricCard
            testId="prod-revenue-total"
            label="60-day Revenue"
            value={fmtUSD(summary.total_revenue_usd)}
            delta={fmtTonnes(summary.total_tonnes)}
            deltaLabel="Total tonnage"
            accent="success"
          />
        </div>
      )}

      {/* Progress bar */}
      {summary && (
        <div className="phizo-card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="phizo-label">Today's Progress vs Target</div>
              <div className="font-heading text-xl font-bold text-[#09090b] mt-1 flex items-center gap-2">
                <Target size={18} weight="bold" /> {fmtTonnes(todayCard.tonnes)} / {fmtTonnes(target)}
              </div>
            </div>
            <div className={`font-heading text-4xl font-black tracking-tighter ${todayCard.progress_pct >= 100 ? "text-[#00A142]" : todayCard.progress_pct >= 75 ? "text-[#002FA7]" : "text-[#FF2A2A]"}`}>
              {todayCard.progress_pct}%
            </div>
          </div>
          <div className="h-3 w-full bg-[#f4f4f5] border border-[#e4e4e7] overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${Math.min(todayCard.progress_pct, 100)}%`,
                background: todayCard.progress_pct >= 100 ? "#00C853" : todayCard.progress_pct >= 75 ? "#002FA7" : "#FFC000",
              }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#e4e4e7] mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`prod-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[#002FA7] text-[#09090b]"
                : "border-transparent text-[#52525b] hover:text-[#09090b]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "log" && (
        <div className="phizo-card">
          {records.length === 0 ? <div className="p-5"><Empty label="No production logged yet." /></div> : (
            <div className="overflow-x-auto">
              <table className="phizo-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Product</th><th>Shift</th>
                    <th className="text-right">Tonnes</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Value (USD)</th>
                    <th>Logged By</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} data-testid={`prod-row-${r.id}`}>
                      <td className="mono text-sm">{fmtDate(r.record_date)}</td>
                      <td className="font-medium">{r.product_name}</td>
                      <td className="text-xs uppercase tracking-wider text-[#52525b]">{r.shift}</td>
                      <td className="text-right mono font-semibold">{fmtTonnes(r.tonnes)}</td>
                      <td className="text-right mono">{fmtUSD(r.rate_usd)}</td>
                      <td className="text-right mono font-semibold">{fmtUSD(r.tonnes * r.rate_usd)}</td>
                      <td className="text-sm text-[#52525b]">{r.logged_by_name}</td>
                      <td className="text-right">
                        {canManage && (
                          <BtnGhost onClick={() => deleteRecord(r)} className="text-[#FF2A2A]">
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "delivery" && (
        <div className="phizo-card">
          <div className="p-3 border-b border-[#e4e4e7] flex justify-end">
            <BtnSecondary onClick={() => exportCSV(deliveries, `delivery-notes-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> Export CSV
            </BtnSecondary>
          </div>
          {deliveries.length === 0 ? <div className="p-5"><Empty label="No delivery notes yet." /></div> : (
            <div className="overflow-x-auto">
              <table className="phizo-table">
                <thead>
                  <tr>
                    <th>Number</th><th>Delivery Date</th><th>Customer</th>
                    <th>Product</th><th className="text-right">Tonnes</th>
                    <th className="text-right">Value</th>
                    <th>Truck</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`dn-row-${d.number}`}
                      onClick={() => nav(`/delivery-notes/${d.id}`)}
                      className="cursor-pointer hover:bg-[#f4f4f5]"
                    >
                      <td className="mono font-semibold">{d.number}</td>
                      <td className="text-sm">{fmtDate(d.delivery_date)}</td>
                      <td className="font-medium">{d.customer}</td>
                      <td className="text-sm">{d.product_name}</td>
                      <td className="text-right mono font-semibold">{fmtTonnes(d.tonnes)}</td>
                      <td className="text-right mono">{fmtUSD(typeof d.rate_usd === "string" ? d.rate_usd : d.tonnes * d.rate_usd)}</td>
                      <td className="text-sm mono">{d.truck_plate || "—"}</td>
                      <td><StatusBadge status={d.status === "voided" ? "cancelled" : d.status === "confirmed" ? "approved" : d.status === "delivered" ? "pending" : "draft"} /></td>
                      <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <BtnGhost onClick={() => setViewDn(d)} data-testid={`dn-view-${d.number}`}>
                          <Eye size={12} weight="bold" />
                        </BtnGhost>
                        <Link to={`/delivery-notes/${d.id}/print`} target="_blank" rel="noreferrer">
                          <BtnGhost data-testid={`dn-print-${d.number}`}>
                            <Printer size={12} weight="bold" />
                          </BtnGhost>
                        </Link>
                        {d.status === "delivered" && canEdit && (
                          <BtnGhost
                            onClick={() => { setConfirmOpen(d); setConfirmForm({ roads_team_signoff: "", contract_manager_signoff: "" }); }}
                            data-testid={`dn-confirm-${d.number}`}
                          >
                            <CheckCircle size={12} weight="bold" /> Confirm
                          </BtnGhost>
                        )}
                        {canManage && d.status !== "voided" && (
                          <BtnGhost onClick={() => voidDn(d)} className="text-[#FF2A2A]">Void</BtnGhost>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "shifts" && (
        <div className="phizo-card" data-testid="shifts-tab">
          <div className="p-5 border-b border-[#e4e4e7]">
            <div className="phizo-label">Shift Totaliser Logs</div>
            <div className="font-heading text-xl font-bold mt-1">Per-shift Crushing — last 14 days</div>
            <p className="text-xs text-[#52525b] mt-1 leading-relaxed">
              Each crusher supervisor logs the starting and ending totaliser readings for their shift.
              The system computes tonnes crushed, flags shifts below target, and requires a justification when missed.
              Use <strong>Send 24h Report</strong> to email the contract manager a 07:00 → 07:00 summary.
            </p>
            {cronStatus && (
              <div className="mt-3 flex flex-wrap items-center gap-3 p-3 bg-[#fafafa] border border-[#e4e4e7]" data-testid="cron-status">
                <div className="phizo-label flex items-center gap-1.5">
                  <span className={`inline-block w-2 h-2 rounded-full ${cronStatus.enabled ? "bg-[#00A142]" : "bg-[#a1a1aa]"}`} />
                  Auto-send {cronStatus.enabled ? "ON" : "OFF"}
                </div>
                <div className="text-xs text-[#52525b]">
                  Fires daily at <strong className="mono">{cronStatus.fire_local_time}</strong> Zambia time
                  (UTC{cronStatus.tz_hours >= 0 ? "+" : ""}{cronStatus.tz_hours})
                </div>
                <div className="text-xs text-[#52525b]">
                  Next: <strong className="mono">{new Date(cronStatus.next_run_local).toLocaleString("en-GB")}</strong>
                </div>
                {cronStatus.last_run ? (
                  <div className="text-xs text-[#52525b]">
                    Last: <strong className="mono">{new Date(cronStatus.last_run.run_at).toLocaleString("en-GB")}</strong>
                    {" "}— {cronStatus.last_run.recipients || 0} recipients,
                    {" "}{cronStatus.last_run.shifts || 0} shifts,
                    {" "}{cronStatus.last_run.total_tonnes || 0} t
                  </div>
                ) : (
                  <div className="text-xs text-[#a1a1aa]">No automated run yet</div>
                )}
              </div>
            )}
          </div>
          {shifts.length === 0 ? (
            <div className="p-10"><Empty label="No shift logs yet. Click 'Log Shift' to record one." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="phizo-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Shift</th><th>Supervisor</th><th>Product</th>
                    <th className="text-right">Tot. start</th>
                    <th className="text-right">Tot. end</th>
                    <th className="text-right">Tonnes</th>
                    <th className="text-right">Target</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((s) => (
                    <React.Fragment key={s.id}>
                      <tr data-testid={`shift-row-${s.id}`}>
                        <td className="mono">{fmtDate(s.shift_date)}</td>
                        <td className="capitalize font-semibold">{s.shift}</td>
                        <td>{s.supervisor_name}</td>
                        <td>{s.product_name}</td>
                        <td className="text-right mono">{s.totalizer_start}</td>
                        <td className="text-right mono">{s.totalizer_end}</td>
                        <td className="text-right mono font-bold">{s.tonnes} t</td>
                        <td className="text-right mono text-[#52525b]">{s.target_tonnes} t</td>
                        <td>
                          {s.met_target ? (
                            <span className="text-[10px] mono uppercase tracking-wider px-2 py-0.5 bg-[#E6F7EE] text-[#00A142] font-bold">on target</span>
                          ) : (
                            <span className="text-[10px] mono uppercase tracking-wider px-2 py-0.5 bg-[#FFF8E1] text-[#B58C00] font-bold">missed</span>
                          )}
                        </td>
                      </tr>
                      {!s.met_target && s.justification && (
                        <tr className="bg-[#FFFBE6]">
                          <td colSpan={9} className="text-xs text-[#52525b] italic px-4 py-2">
                            <strong className="text-[#09090b]">Justification:</strong> {s.justification}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {tab === "report" && summary && (
        <div className="space-y-4">
          <div className="phizo-card p-5">
            <div className="phizo-label mb-2">Daily vs Target (60 days)</div>
            <div className="font-heading text-xl font-bold mb-4">Production Trend</div>
            {summary.daily_series.length === 0 ? (
              <Empty label="Log production to populate this chart." />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={summary.daily_series}>
                  <CartesianGrid stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="day" stroke="#a1a1aa" fontSize={11} />
                  <YAxis stroke="#a1a1aa" fontSize={11} />
                  <Tooltip contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                    formatter={(v) => fmtTonnes(v)} />
                  <ReferenceLine y={target} stroke="#FF2A2A" strokeDasharray="4 2" label={{ value: "Target", position: "right", fill: "#FF2A2A", fontSize: 11 }} />
                  <Bar dataKey="total" fill="#002FA7" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="phizo-card p-5">
            <div className="phizo-label mb-2">Cumulative Tonnage</div>
            <div className="font-heading text-xl font-bold mb-4">Running Total</div>
            {summary.cumulative_series.length === 0 ? (
              <Empty label="No data." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={summary.cumulative_series}>
                  <CartesianGrid stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="day" stroke="#a1a1aa" fontSize={11} />
                  <YAxis stroke="#a1a1aa" fontSize={11} />
                  <Tooltip contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                    formatter={(v) => fmtTonnes(v)} />
                  <Line type="linear" dataKey="cumulative" stroke="#002FA7" strokeWidth={2}
                    dot={{ fill: "#002FA7", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="phizo-card">
            <div className="p-5 border-b border-[#e4e4e7] flex justify-between items-center">
              <div>
                <div className="phizo-label">By Product (last 60 days)</div>
                <div className="font-heading text-xl font-bold mt-1">Invoice-ready Totals</div>
              </div>
              <BtnSecondary onClick={() => exportCSV(summary.by_product, `production-summary-${Date.now()}.csv`)}>
                <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> Export CSV
              </BtnSecondary>
            </div>
            <table className="phizo-table">
              <thead><tr><th>Code</th><th>Name</th><th className="text-right">Rate</th><th className="text-right">Tonnes</th><th className="text-right">Revenue (USD)</th></tr></thead>
              <tbody>
                {summary.by_product.map((p) => (
                  <tr key={p.code}>
                    <td className="mono font-semibold">{p.code}</td>
                    <td>{p.name}</td>
                    <td className="text-right mono">{fmtUSD(p.rate_usd)}</td>
                    <td className="text-right mono font-semibold">{fmtTonnes(p.tonnes)}</td>
                    <td className="text-right mono font-semibold">{fmtUSD(p.revenue_usd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#09090b] text-white">
                  <td colSpan={3} className="p-3 phizo-label text-white">Total</td>
                  <td className="p-3 text-right mono font-bold">{fmtTonnes(summary.total_tonnes)}</td>
                  <td className="p-3 text-right font-heading font-black text-lg">{fmtUSD(summary.total_revenue_usd)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Log production modal */}
      <Modal open={logOpen} onClose={() => setLogOpen(false)} title="Log Daily Production">
        <form onSubmit={submitLog} className="space-y-4">
          <Field label="Date">
            <Input type="date" required data-testid="log-date"
              value={logForm.record_date}
              onChange={(e) => setLogForm({ ...logForm, record_date: e.target.value })} />
          </Field>
          <Field label="Product">
            <Select required data-testid="log-product"
              value={logForm.product_code}
              onChange={(e) => setLogForm({ ...logForm, product_code: e.target.value })}>
              <option value="">Select product…</option>
              {products.map((p) => (
                <option key={p.code} value={p.code}>{`${p.code} — ${p.name} (${fmtUSD(p.rate_usd)}/t)`}</option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tonnes Produced">
              <Input type="number" step="0.1" min="0" required
                data-testid="log-tonnes"
                value={logForm.tonnes}
                onChange={(e) => setLogForm({ ...logForm, tonnes: e.target.value })} />
            </Field>
            <Field label="Shift">
              <Select value={logForm.shift} onChange={(e) => setLogForm({ ...logForm, shift: e.target.value })}>
                <option value="full">Full Day</option>
                <option value="day">Day Shift</option>
                <option value="night">Night Shift</option>
              </Select>
            </Field>
          </div>
          <Field label="Notes">
            <Textarea value={logForm.notes}
              onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} />
          </Field>
          <Field label="Project / Cost-centre (optional)">
            <ProjectSelect value={logForm.project_code}
              onChange={(v) => setLogForm({ ...logForm, project_code: v })}
              testId="log-project" />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setLogOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="log-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>

      <Modal open={shiftOpen} onClose={() => setShiftOpen(false)} title="Log Crusher Shift" wide testId="shift-modal">
        <form onSubmit={submitShift} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Shift Date">
              <Input type="date" required value={shiftForm.shift_date}
                onChange={(e) => setShiftForm({ ...shiftForm, shift_date: e.target.value })}
                data-testid="shift-date" />
            </Field>
            <Field label="Shift">
              <Select value={shiftForm.shift}
                onChange={(e) => setShiftForm({ ...shiftForm, shift: e.target.value })}
                data-testid="shift-which">
                <option value="day">Day (06:00 – 18:00)</option>
                <option value="night">Night (18:00 – 06:00)</option>
              </Select>
            </Field>
            <Field label="Product">
              <Select required value={shiftForm.product_code}
                onChange={(e) => setShiftForm({ ...shiftForm, product_code: e.target.value })}
                data-testid="shift-product">
                <option value="">Select…</option>
                {products.map((p) => <option key={p.code} value={p.code}>{`${p.name} (${p.code})`}</option>)}
              </Select>
            </Field>
            <Field label="Shift Target (tonnes)">
              <Input type="number" min="0" step="1" value={shiftForm.target_tonnes}
                onChange={(e) => setShiftForm({ ...shiftForm, target_tonnes: e.target.value })}
                data-testid="shift-target" />
            </Field>
            <Field label="Totaliser START (cumulative)">
              <Input type="number" min="0" step="0.01" required value={shiftForm.totalizer_start}
                onChange={(e) => setShiftForm({ ...shiftForm, totalizer_start: e.target.value })}
                data-testid="shift-totalizer-start" />
            </Field>
            <Field label="Totaliser END (cumulative)">
              <Input type="number" min="0" step="0.01" required value={shiftForm.totalizer_end}
                onChange={(e) => setShiftForm({ ...shiftForm, totalizer_end: e.target.value })}
                data-testid="shift-totalizer-end" />
            </Field>
          </div>
          <div className="bg-[#fafafa] border border-[#e4e4e7] p-3 flex items-center justify-between">
            <div className="phizo-label">Computed tonnage</div>
            <div className="font-heading text-2xl font-black">
              {(Number(shiftForm.totalizer_end) - Number(shiftForm.totalizer_start)).toFixed(2)} t
            </div>
          </div>
          {(Number(shiftForm.totalizer_end) - Number(shiftForm.totalizer_start)) < Number(shiftForm.target_tonnes) && (
            <Field label="Justification (REQUIRED — shift is below target)" hint="Will be emailed to all managers automatically.">
              <Textarea required
                value={shiftForm.justification}
                onChange={(e) => setShiftForm({ ...shiftForm, justification: e.target.value })}
                placeholder="Belt slip, planned maintenance, power outage, raw feed shortage…"
                data-testid="shift-justification"
              />
            </Field>
          )}
          <Field label="Notes (optional)">
            <Textarea
              value={shiftForm.notes}
              onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setShiftOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="shift-save">Save Shift</BtnPrimary>
          </div>
        </form>
      </Modal>


      {/* Delivery note create modal */}
      <Modal open={dnOpen} onClose={() => setDnOpen(false)} title="New Delivery Note" wide>
        <form onSubmit={submitDn} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Delivery Date" hint="Usually previous day's production">
              <Input type="date" required data-testid="dn-date"
                value={dnForm.delivery_date}
                onChange={(e) => setDnForm({ ...dnForm, delivery_date: e.target.value })} />
            </Field>
            <Field label="Product">
              <Select required data-testid="dn-product"
                value={dnForm.product_code}
                onChange={(e) => setDnForm({ ...dnForm, product_code: e.target.value })}>
                <option value="">Select product…</option>
                {products.map((p) => (
                  <option key={p.code} value={p.code}>{`${p.code} — ${p.name}`}</option>
                ))}
              </Select>
            </Field>
            <Field label="Customer / End User" hint="Pick from saved customers or type a new one">
              <SmartInput
                required value={dnForm.customer}
                testId="dn-customer"
                placeholder="e.g. Roads Department"
                onChange={(v) => setDnForm({ ...dnForm, customer: v })}
                options={customers.map((c) => ({ value: c.name, hint: c.code }))}
              />
            </Field>
            <Field label="Project / Cost-centre (optional)">
              <ProjectSelect value={dnForm.project_code}
                onChange={(v) => setDnForm({ ...dnForm, project_code: v })}
                testId="dn-project" />
            </Field>
            <Field label="Project (free text — optional)">
              <Input value={dnForm.project}
                onChange={(e) => setDnForm({ ...dnForm, project: e.target.value })} />
            </Field>
            <Field label="Tonnes Delivered">
              <Input type="number" step="0.1" min="0" required
                data-testid="dn-tonnes"
                value={dnForm.tonnes}
                onChange={(e) => setDnForm({ ...dnForm, tonnes: e.target.value })} />
            </Field>
            <Field label="Truck Plate">
              <Input value={dnForm.truck_plate}
                onChange={(e) => setDnForm({ ...dnForm, truck_plate: e.target.value })} />
            </Field>
            <Field label="Driver Name">
              <Input value={dnForm.driver_name}
                onChange={(e) => setDnForm({ ...dnForm, driver_name: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea value={dnForm.notes}
              onChange={(e) => setDnForm({ ...dnForm, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setDnOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="dn-save">Create & Print</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* Confirm sign-off */}
      <Modal open={!!confirmOpen} onClose={() => setConfirmOpen(null)}
        title={confirmOpen ? `Record Sign-off — ${confirmOpen.number}` : ""}>
        <form onSubmit={confirmDn} className="space-y-4">
          <div className="phizo-surface p-3 text-sm">
            <div className="phizo-label mb-1">Delivery</div>
            {confirmOpen && (
              <div>
                {confirmOpen.customer} — {fmtTonnes(confirmOpen.tonnes)} of {confirmOpen.product_name}
              </div>
            )}
          </div>
          <Field label="Roads Team Sign-off (Name)">
            <Input required data-testid="dn-roads"
              placeholder="Name of person from roads team"
              value={confirmForm.roads_team_signoff}
              onChange={(e) => setConfirmForm({ ...confirmForm, roads_team_signoff: e.target.value })} />
          </Field>
          <Field label="Contract Manager Sign-off (Name)">
            <Input required data-testid="dn-mgr"
              placeholder="Contract manager name"
              value={confirmForm.contract_manager_signoff}
              onChange={(e) => setConfirmForm({ ...confirmForm, contract_manager_signoff: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setConfirmOpen(null)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="dn-confirm-save">Record Sign-off</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* View delivery note */}
      <Modal open={!!viewDn} onClose={() => setViewDn(null)}
        title={viewDn ? `Delivery Note ${viewDn.number}` : ""} wide>
        {viewDn && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="phizo-label">Customer</div><div className="font-semibold">{viewDn.customer}</div></div>
              <div><div className="phizo-label">Delivery Date</div><div>{fmtDate(viewDn.delivery_date)}</div></div>
              <div><div className="phizo-label">Product</div><div>{viewDn.product_name}</div></div>
              <div><div className="phizo-label">Tonnes</div><div className="font-heading text-xl font-black">{fmtTonnes(viewDn.tonnes)}</div></div>
              <div><div className="phizo-label">Rate</div><div className="mono">{fmtUSD(viewDn.rate_usd)}/t</div></div>
              <div><div className="phizo-label">Value</div><div className="font-heading text-xl font-black">{fmtUSD(viewDn.tonnes * viewDn.rate_usd)}</div></div>
              <div><div className="phizo-label">Truck</div><div className="mono">{viewDn.truck_plate || "—"}</div></div>
              <div><div className="phizo-label">Driver</div><div>{viewDn.driver_name || "—"}</div></div>
            </div>
            {viewDn.signed_at && (
              <div className="phizo-surface p-3">
                <div className="phizo-label mb-2">Sign-off</div>
                <div>Roads Team: <strong>{viewDn.roads_team_signoff}</strong></div>
                <div>Contract Manager: <strong>{viewDn.contract_manager_signoff}</strong></div>
                <div className="text-xs text-[#a1a1aa] mt-1">at {fmtDateTime(viewDn.signed_at)}</div>
              </div>
            )}
            <div className="flex justify-end pt-3 border-t border-[#e4e4e7] gap-2">
              <button
                type="button"
                data-testid="dn-email"
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/delivery-notes/${viewDn.id}/email-payload`);
                    setEmailDn({
                      dn: viewDn,
                      to: data.to || "",
                      subject: data.subject,
                      preview: data.body,
                    });
                    if (!data.has_email) {
                      toast.message("No email on file for this customer — you can type one manually.");
                    }
                  } catch (e) { toast.error(formatError(e)); }
                }}
                className="border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-medium text-[#09090b] hover:bg-[#f4f4f5] transition-colors">
                Email to Customer
              </button>
              <Link to={`/delivery-notes/${viewDn.id}/print`} target="_blank" rel="noreferrer">
                <BtnPrimary><Printer size={14} weight="bold" className="inline mr-1" /> Print Delivery Note</BtnPrimary>
              </Link>
            </div>
          </div>
        )}
      </Modal>

      {/* Send DN email */}
      <SendEmailDialog
        open={!!emailDn}
        onClose={() => setEmailDn(null)}
        title={emailDn ? `Email Delivery Note ${emailDn.dn.number}` : "Email"}
        endpoint={emailDn ? `/delivery-notes/${emailDn.dn.id}/send-email` : ""}
        initialTo={emailDn?.to || ""}
        initialSubject={emailDn?.subject || ""}
        bodyPreview={emailDn?.preview || ""}
        masterCcHint="team@phizoquarry.com"
      />
    </div>
  );
}
